import type { StoryNpc } from '@rdd/contracts'
import { format } from '../i18n/format'
import { t } from '../i18n/pt-BR'

/**
 * A conversa com o NPC (modo_historia_mundos/05#HM-002 e 06#HM-001). docs/21 secoes 12 e 13. DOM sobre o canvas:
 *
 * - **o convite**: com um NPC ao alcance e a conversa fechada, um botao "Falar com ..." -- o toque, para quem
 *   nao tem a tecla;
 * - **a conversa**: o retrato, o nome e uma das falas de cumprimento; com `botId`, o botao "Duelar";
 * - **o desafio**: a fala `challenge`, com "Duelar agora" e "Agora nao";
 * - **o aviso**: que o duelo esta sendo preparado, ou por que ele nao saiu.
 *
 * A fala e conteudo (docs/21 secao 12); so os botoes passam pelo dicionario.
 */

export type NpcDialog = {
  element: HTMLElement
  /** O NPC ao alcance mudou. `null` esconde o convite. */
  reach(npc: StoryNpc | null, onTalk: (npc: StoryNpc) => void): void
  open(npc: StoryNpc, portraitUrl: string, onDuel: (() => void) | null): void
  notice(text: string): void
  /** Uma fala do NPC, com o retrato, sem oferecer nada: a de vitoria ou derrota, na volta do duelo. */
  speak(npc: StoryNpc, portraitUrl: string, text: string): void
  close(): void
  readonly isOpen: boolean
}

export function npcDialog(pick: (count: number) => number = (count) => Math.floor(Math.random() * count)): NpcDialog {
  const element = document.createElement('section')
  element.className = 'npc'
  element.setAttribute('aria-live', 'polite')
  const invite = document.createElement('button')
  invite.type = 'button'
  invite.className = 'button npc-invite'
  invite.hidden = true
  const box = document.createElement('div')
  box.className = 'npc-box'
  box.hidden = true
  element.append(invite, box)

  let reached: StoryNpc | null = null
  let talk: (npc: StoryNpc) => void = () => undefined
  invite.addEventListener('click', () => {
    if (reached !== null) talk(reached)
  })

  const button = (text: string, onClick: () => void, secondary = false): HTMLButtonElement => {
    const b = document.createElement('button')
    b.type = 'button'
    b.className = secondary ? 'button button-secondary' : 'button'
    b.textContent = text
    b.addEventListener('click', onClick)
    return b
  }
  const speech = (text: string): HTMLParagraphElement => {
    const p = document.createElement('p')
    p.textContent = text
    return p
  }
  const show = (...children: HTMLElement[]): void => {
    box.replaceChildren(...children)
    box.hidden = false
    invite.hidden = true
    box.setAttribute('role', 'dialog')
  }
  const header = (npc: StoryNpc, portraitUrl: string): HTMLElement => {
    const head = document.createElement('header')
    const img = document.createElement('img')
    img.src = portraitUrl
    img.alt = ''
    const name = document.createElement('h2')
    name.textContent = npc.name
    head.append(img, name)
    return head
  }
  const actions = (...buttons: HTMLButtonElement[]): HTMLElement => {
    const row = document.createElement('div')
    row.className = 'npc-actions'
    row.append(...buttons)
    return row
  }

  const dialog: NpcDialog = {
    element,
    get isOpen() {
      return !box.hidden
    },
    reach(npc, onTalk) {
      reached = npc
      talk = onTalk
      invite.textContent = npc === null ? '' : format(t.npc.talk, { name: npc.name })
      invite.hidden = npc === null || dialog.isOpen
      // Saiu do alcance com a conversa aberta: ela fecha, como fecharia ao virar as costas.
      if (npc === null) dialog.close()
    },
    open(npc, portraitUrl, onDuel) {
      const greeting = npc.lines.greeting[pick(npc.lines.greeting.length)] ?? npc.lines.greeting[0] ?? ''
      const leave = button(t.npc.leave, () => dialog.close(), true)
      const challenge = npc.lines.challenge
      if (onDuel === null || challenge === undefined) {
        show(header(npc, portraitUrl), speech(greeting), actions(leave))
        return
      }
      const duel = button(t.npc.duel, () =>
        show(
          header(npc, portraitUrl),
          speech(challenge),
          actions(
            button(t.npc.accept, () => {
              dialog.notice(t.npc.starting)
              onDuel()
            }),
            button(t.npc.notNow, () => dialog.close(), true),
          ),
        ),
      )
      show(header(npc, portraitUrl), speech(greeting), actions(duel, leave))
    },
    speak(npc, portraitUrl, text) {
      show(header(npc, portraitUrl), speech(text), actions(button(t.npc.leave, () => dialog.close(), true)))
    },
    notice(text) {
      show(speech(text), actions(button(t.npc.leave, () => dialog.close(), true)))
    },
    close() {
      box.replaceChildren()
      box.hidden = true
      box.removeAttribute('role')
      invite.hidden = reached === null
    },
  }
  return dialog
}
